import { normalizeSourceUrl } from "@/lib/source-url";

const MAX_UNWRAP_DEPTH = 5;
const GSTATIC_FAVICON_HOST_RE = /^t\d+\.gstatic\.com$/i;
const FACEBOOK_IMAGE_HOST_SUFFIXES = ["fbcdn.net", "fbsbx.com"] as const;
const FACEBOOK_PAGE_HOST_SUFFIXES = [
  "facebook.com",
  "fb.com",
  "facebook.net",
  "messenger.com",
] as const;

const PROXY_PARAM_KEYS = ["url", "u", "src", "image", "domain_url", "domain"];
export const LOW_QUALITY_IMAGE_URL_PATTERNS = [
  /icon/i,
  /logo/i,
  /favicon/i,
  /thumbnail/i,
  /thumb/i,
  /avatar/i,
  /badge/i,
  /button/i,
  /banner/i,
  /ads?[/-]/i,
  /sponsor/i,
  /1x1/i,
  /pixel/i,
  /tracking/i,
  /\.svg(?:$|[?#])/i,
  /\/emoji\//i,
  /\/icon[s]?\//i,
  /\/assets\/[^/]*icon/i,
  /placeholder/i,
  /spinner/i,
  /loading/i,
  /blank\./i,
  /transparent\./i,
  /tiny/i,
  /sm\./i,
  /xs\./i,
] as const;
export const MIN_IMAGE_WIDTH = 960;
export const MIN_IMAGE_HEIGHT = 540;
export const MIN_IMAGE_ASPECT_RATIO = 0.5;
export const MAX_IMAGE_ASPECT_RATIO = 3;

const maybeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const tryParseHttpUrl = (value: string): URL | null => {
  try {
    const parsed = new URL(value);
    return /^https?:$/i.test(parsed.protocol) ? parsed : null;
  } catch {
    return null;
  }
};

const isGoogleFaviconProxy = (url: URL): boolean => {
  const host = url.hostname.toLowerCase();
  const path = url.pathname.toLowerCase();

  if (
    (host === "google.com" || host === "www.google.com") &&
    path.startsWith("/s2/favicons")
  ) {
    return true;
  }

  if (GSTATIC_FAVICON_HOST_RE.test(host) && path.includes("favicon")) {
    return true;
  }

  return false;
};

const isKnownImageProxy = (url: URL): boolean => {
  const host = url.hostname.toLowerCase();
  return (
    host === "wsrv.nl" ||
    host.endsWith(".wsrv.nl") ||
    isGoogleFaviconProxy(url)
  );
};

const hostMatchesSuffix = (
  host: string,
  suffixes: readonly string[]
): boolean => {
  const normalized = host.toLowerCase();
  return suffixes.some(
    (suffix) => normalized === suffix || normalized.endsWith(`.${suffix}`)
  );
};

const unwrapKnownProxyUrl = (raw: string): URL | null => {
  let candidate = raw.trim();
  if (!candidate) return null;

  if (candidate.startsWith("//")) {
    candidate = `https:${candidate}`;
  }

  for (let depth = 0; depth < MAX_UNWRAP_DEPTH; depth += 1) {
    const parsed = tryParseHttpUrl(candidate);
    if (!parsed) return null;
    if (!isKnownImageProxy(parsed)) return parsed;

    let nextCandidate: string | null = null;

    for (const key of PROXY_PARAM_KEYS) {
      const value = parsed.searchParams.get(key);
      if (!value) continue;

      let decoded = value.trim();
      for (let i = 0; i < 2; i += 1) {
        const maybeDecoded = maybeDecode(decoded);
        if (maybeDecoded === decoded) break;
        decoded = maybeDecoded;
      }

      if (decoded.startsWith("//")) {
        decoded = `https:${decoded}`;
      } else if (key === "domain" || key === "domain_url") {
        decoded = decoded.startsWith("http")
          ? decoded
          : `https://${decoded.replace(/^\/*/, "")}`;
      }

      if (tryParseHttpUrl(decoded)) {
        nextCandidate = decoded;
        break;
      }
    }

    if (!nextCandidate) {
      return parsed;
    }

    candidate = nextCandidate;
  }

  return tryParseHttpUrl(candidate);
};

export const isFacebookImageHost = (host: string): boolean =>
  hostMatchesSuffix(host, FACEBOOK_IMAGE_HOST_SUFFIXES);

export const isFacebookPageHost = (host: string): boolean =>
  hostMatchesSuffix(host, FACEBOOK_PAGE_HOST_SUFFIXES);

export const isBlockedImageUrl = (
  raw: string | null | undefined
): boolean => {
  if (!raw) return false;
  const trimmed = raw.trim();
  if (!trimmed || trimmed.startsWith("data:") || trimmed.startsWith("/")) {
    return false;
  }

  const target = unwrapKnownProxyUrl(trimmed);
  if (!target) return false;

  return (
    isFacebookImageHost(target.hostname) || isFacebookPageHost(target.hostname)
  );
};

export const sanitizeImageUrl = (
  raw: string | null | undefined
): string | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  return isBlockedImageUrl(trimmed) ? null : trimmed;
};

export const matchesLowQualityImageUrlPattern = (
  raw: string | null | undefined
): boolean => {
  const sanitized = sanitizeImageUrl(raw);
  if (!sanitized) return true;

  return LOW_QUALITY_IMAGE_URL_PATTERNS.some((pattern) =>
    pattern.test(sanitized)
  );
};

export const meetsMinimumImageDimensions = (
  width: number,
  height: number
): boolean => {
  if (!Number.isFinite(width) || !Number.isFinite(height)) return false;
  if (width < MIN_IMAGE_WIDTH || height < MIN_IMAGE_HEIGHT) return false;

  const aspectRatio = width / height;
  return (
    aspectRatio >= MIN_IMAGE_ASPECT_RATIO &&
    aspectRatio <= MAX_IMAGE_ASPECT_RATIO
  );
};

export const getSafeFaviconUrl = (
  pageUrl: string | null | undefined,
  size: number = 64
): string | null => {
  const normalizedPageUrl = normalizeSourceUrl(pageUrl);
  if (!normalizedPageUrl) return null;

  try {
    const pageHost = new URL(normalizedPageUrl).hostname.toLowerCase();
    if (isFacebookPageHost(pageHost) || isFacebookImageHost(pageHost)) {
      return null;
    }

    return `https://www.google.com/s2/favicons?sz=${size}&domain_url=${encodeURIComponent(
      normalizedPageUrl
    )}`;
  } catch {
    return null;
  }
};

export const sanitizeImageList = <T extends string | null | undefined>(
  values: T[]
): string[] =>
  values
    .map((value) => sanitizeImageUrl(value))
    .filter((value): value is string => Boolean(value));
